import { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stAgentsConfig,
  stErrorMessage,
  stUpdateAgentsConfig,
  StAgentsConfig,
} from '../../../services/socialTradingService';
import { stText } from '../StListUi';

export default function SocialTradingAgentsConfigList() {
  const t = useTranslate();
  const notify = useNotify();
  const [cfg, setCfg] = useState<StAgentsConfig | null>(null);
  const [cardValidHours, setCardValidHours] = useState('');
  const [strategyIdsText, setStrategyIdsText] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await stAgentsConfig();
      setCfg(data || null);
      setCardValidHours(data?.cardValidHours != null ? String(data.cardValidHours) : '24');
      setStrategyIdsText((data?.strategyIds || []).join('\n'));
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      const n = Number(cardValidHours.trim());
      if (!Number.isInteger(n) || n <= 0) {
        notify(t('module.socialTrading.agentsConfigHoursInvalid'), { type: 'warning' });
        return;
      }
      const strategyIds = strategyIdsText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!strategyIds.length) {
        notify(t('module.socialTrading.agentsConfigStrategyIdsInvalid'), { type: 'warning' });
        return;
      }
      const next = await stUpdateAgentsConfig({ cardValidHours: n, strategyIds });
      setCfg(next || null);
      setCardValidHours(next?.cardValidHours != null ? String(next.cardValidHours) : cardValidHours);
      setStrategyIdsText((next?.strategyIds || strategyIds).join('\n'));
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <PermissionGuard resource="st-agents-config" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.agentsConfig')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.agentsConfig') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Stack spacing={2} sx={{ mt: 2, maxWidth: 640 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('module.socialTrading.agentsConfigCardTtl')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('module.socialTrading.agentsConfigHint')}
              </Typography>
              {cfg && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('module.socialTrading.agentsConfigCurrent', {
                    hours: stText(cfg.cardValidHours),
                  })}
                </Typography>
              )}
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label={t('module.socialTrading.cardValidHours')}
                  value={cardValidHours}
                  onChange={(e) => setCardValidHours(e.target.value)}
                />
                <TextField
                  size="small"
                  label={t('module.socialTrading.agentsConfigStrategyIds')}
                  value={strategyIdsText}
                  onChange={(e) => setStrategyIdsText(e.target.value)}
                  multiline
                  minRows={6}
                  helperText={t('module.socialTrading.agentsConfigStrategyIdsHint')}
                />
                {/* POST /social-trading/agents-config requires st-agents-config:update OR st-agents:update */}
                <PermissionGuard
                  anyOf={[
                    { resource: 'st-agents-config', action: 'update' },
                    { resource: 'st-agents', action: 'update' },
                  ]}
                  fallback={null}
                  loadingFallback={null}
                >
                  <Button variant="contained" onClick={save} sx={{ alignSelf: 'flex-start' }}>
                    {t('module.socialTrading.save')}
                  </Button>
                </PermissionGuard>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
}
